import { applyFontVariants } from '../hepler/functions';

export const isSequentialLinkingEnabled = (sdkOptions) => {
	const opts = sdkOptions ?? global.sdkOptions;
	return (
		opts?.sequentialLinking === true ||
		opts?.sequentialLinking === 'true'
	);
};

export const isShowSharingDetailsLinkEnabled = (sdkOptions) => {
	const opts = sdkOptions ?? global.sdkOptions;
	return (
		opts?.showSharingDetailsLink === true ||
		opts?.showSharingDetailsLink === 'true'
	);
};

export const is2FAEnabled = (sdkOptions) => {
	// If boolean directly passed
	if (typeof sdkOptions === 'boolean') {
		return sdkOptions;
	}
	// If string directly passed
	if (typeof sdkOptions === 'string') {
		const lower = sdkOptions.toLowerCase();
		return lower === 'true' || lower === 'y' || lower === '1';
	}

	// Resolve sdkOptions from argument (could be sdkOptions, customDetails, or entityConfig)
	const resolvedOptions =
		sdkOptions?.sdkOptions ||
		sdkOptions?.entityConfig?.sdkOptions ||
		sdkOptions ||
		global.sdkOptions ||
		(typeof window !== 'undefined' ? window.sdkOptions : undefined);

	// Check URL query parameters (case-insensitive check for enable2FA / enable2fa)
	if (typeof window !== 'undefined') {
		const search =
			window.location?.search ||
			(window.location?.hash?.includes('?')
				? window.location.hash.substring(window.location.hash.indexOf('?'))
				: '');
		if (search) {
			const queryParams = new URLSearchParams(search);
			const queryFlag =
				queryParams.get('enable2FA') ??
				queryParams.get('enable2fa') ??
				queryParams.get('is2FAConfigured') ??
				queryParams.get('is2faConfigured');
			if (queryFlag !== null && queryFlag !== undefined) {
				const lowerQuery = String(queryFlag).toLowerCase();
				return lowerQuery === 'true' || lowerQuery === 'y' || lowerQuery === '1';
			}
		}
	}

	// Check resolved options and global variables
	const rawFlag =
		resolvedOptions?.enable2FA ??
		resolvedOptions?.enable2fa ??
		resolvedOptions?.is2FAConfigured ??
		resolvedOptions?.is2faConfigured ??
		resolvedOptions?.is2FAEnabled ??
		resolvedOptions?.is2faEnabled ??
		global.enable2FA ??
		global.enable2fa;

	if (typeof rawFlag === 'boolean') {
		return rawFlag;
	}
	if (typeof rawFlag === 'string') {
		const lowerRaw = rawFlag.toLowerCase();
		return lowerRaw === 'true' || lowerRaw === 'y' || lowerRaw === '1';
	}

	return false;
};

export const processCompleteEntityConfig = (entityConfigResponse, profile = null, currentFips = null) => {
	if (!entityConfigResponse?.entityConfig) {
		return { themeConfig: null, updatedFips: currentFips };
	}

	const { entityConfig } = entityConfigResponse;

	// Process theme configuration
	const themeConfig = processThemeConfig(entityConfigResponse, profile);

	// Process global SDK options with profile support
	processGlobalSdkOptions(entityConfig, profile);

	// Determine updated FIPs
	const updatedFips = determineAutoDiscoveryFips(currentFips);

	return {
		themeConfig,
		updatedFips,
		entityConfig
	};
};

export const processThemeConfig = (entityConfigResponse, profile = null) => {
	if (!entityConfigResponse?.entityConfig) {
		return null;
	}

	const { entityConfig } = entityConfigResponse;
	let processedConfig = {};

	// Process theme configuration
	if (entityConfig.themeConfig) {
		let themeConfig = { ...entityConfig.themeConfig };

		// Handle profile-specific theme overrides
		if (profile && themeConfig.profile) {
			try {
				const profileData = JSON.parse(themeConfig.profile);
				if (profileData[profile]) {
					// Merge profile-specific config with base theme config
					themeConfig = { ...themeConfig, ...profileData[profile] };
				}
			} catch (error) {
				console.warn('Failed to parse profile configuration:', error);
			}
		}

		// Remove the profile string from the final config as it's been processed
		delete themeConfig.profile;

		// Add additional config properties
		themeConfig.excludeFIP = entityConfig.excludeFIP || [];
		themeConfig.sdkOptions = entityConfig.sdkOptions || {};

		processedConfig = themeConfig;

		// Apply font variants if fontUrl exists
		if (themeConfig.fontUrl) {
			applyFontVariants(themeConfig.fontUrl);
		}
	}

	return processedConfig;
};

export const processGlobalSdkOptions = (entityConfig, profile = null) => {
	let sdkOptions = entityConfig?.sdkOptions ? { ...entityConfig.sdkOptions } : {};

	// Handle profile-specific SDK options overrides
	if (profile && sdkOptions.profile) {
		try {
			const profileData = JSON.parse(sdkOptions.profile);
			if (profileData[profile]) {
				// Merge profile-specific SDK options with base SDK options
				sdkOptions = { ...sdkOptions, ...profileData[profile] };
			}
		} catch (error) {
			console.warn('Failed to parse SDK options profile configuration:', error);
		}
	}

	const queryParams = new URLSearchParams(window.location.search);
	
	if (queryParams.get('allowAnyAccountOnNoMatch') === 'true') {
		sdkOptions.allowAnyAccountOnNoMatch = 'true';
		sdkOptions.disabledAddNew = 'false';
	} else if (queryParams.get('disabledAddNew') === 'true') {
		sdkOptions.disabledAddNew = 'true';
		sdkOptions.allowAnyAccountOnNoMatch = 'false';
	}

	const enable2FAQuery = queryParams.get('enable2FA') ?? queryParams.get('enable2fa');
	if (enable2FAQuery !== null) {
		const lowerQuery = String(enable2FAQuery).toLowerCase();
		sdkOptions.enable2FA = lowerQuery === 'true' || lowerQuery === 'y' || lowerQuery === '1';
	}

	const displayOnlyTargetedFipsParam =
		queryParams.get('displayOnlyTargetedFips');
	if (displayOnlyTargetedFipsParam === 'true') {
		sdkOptions.displayOnlyTargetedFips = 'true';
	} else if (displayOnlyTargetedFipsParam === 'false') {
		sdkOptions.displayOnlyTargetedFips = 'false';
	}

	const terminateOnNoAccountFoundParam =
		queryParams.get('terminateOnNoAccountFound');
	if (terminateOnNoAccountFoundParam === 'true') {
		sdkOptions.terminateOnNoAccountFound = 'true';
	} else if (terminateOnNoAccountFoundParam === 'false') {
		sdkOptions.terminateOnNoAccountFound = 'false';
	}

	// Remove the profile string from the final config as it's been processed
	delete sdkOptions.profile;

	// Set global SDK options
	global.sdkOptions = sdkOptions;
	global.isRedirect = sdkOptions.redirect;
	global.manualAccCheck = sdkOptions.manualAccCheck;
	global.disableAutoPreselect = sdkOptions.disableAutoPreselect;
	global.showFaq = sdkOptions.showFaq === true || sdkOptions.showFaq === 'true';
	global.sendBackBtnRes = sdkOptions.sendBackBtnRes;
	global.redirectTimer = sdkOptions.redirectTimer;
	global.disableSubHeading = sdkOptions.disableSubHeading;
	global.noWelcomeText = sdkOptions.noWelcomeText;
	global.disableTrustFooter = sdkOptions.disableTrustFooter;
	global.isFlowChange = sdkOptions.isFlowChange || 'false';
	global.hideAddNew = sdkOptions.hideAddNew === true || sdkOptions.hideAddNew === 'true';
	global.hideFinvuAppDownload = sdkOptions.hideFinvuAppDownload === true || sdkOptions.hideFinvuAppDownload === 'true';
	global.displayOnlyTargetedFips =
		sdkOptions.displayOnlyTargetedFips === true ||
		sdkOptions.displayOnlyTargetedFips === 'true';
	global.terminateOnNoAccountFound =
		sdkOptions.terminateOnNoAccountFound === true ||
		sdkOptions.terminateOnNoAccountFound === 'true';
	// New discovery experience (UI + sequential linking) — opt-in only
	global.sequentialLinking = isSequentialLinkingEnabled(sdkOptions);
	// "See what you are sharing" link on discovery — opt-in only
	global.showSharingDetailsLink = isShowSharingDetailsLinkEnabled(sdkOptions);
	// 2FA / MFA authentication flow — opt-in only
	global.enable2FA = is2FAEnabled(sdkOptions);
	
	// Auto discovery settings
	global.allowAutoDiscovery = sdkOptions.allowAutoDiscovery || 'false';
	global.autoDiscoveryFips = sdkOptions.autoDiscoveryFips || [];
	global.allowMultipleFipDiscovery = sdkOptions.allowMultipleFipDiscovery || 'false';
	
	
	// Other options
	global.priorityConsents = sdkOptions.priorityConsents || '';

	
	// Sequential consent flow setting (defaults to 'false' for backward compatibility)
	global.enableSequentialConsents = sdkOptions.enableSequentialConsents || 'false';

	// Transition overlay duration between sequential consents (milliseconds, default 1500)
	const parsedTransitionTimer = Number(sdkOptions.consentTransitionTimer);
	global.consentTransitionTimer =
		Number.isFinite(parsedTransitionTimer) && parsedTransitionTimer > 0
			? parsedTransitionTimer
			: 1500;

	// Parse JSON strings safely
	if (typeof sdkOptions.alternativeText === 'string') {
		try {
			global.alternativeText = JSON.parse(sdkOptions.alternativeText);
		} catch (error) {
			console.warn('Failed to parse alternativeText:', error);
		}
	}

	if (typeof sdkOptions.consentOptions === 'string') {
		try {
			global.consentOptions = JSON.parse(sdkOptions.consentOptions);
		} catch (error) {
			console.warn('Failed to parse consentOptions:', error);
		}
	}

	// Account restrictions
	global.accountRestrictions = sdkOptions.accountRestrictions;
	if (typeof global.accountRestrictions === 'string') {
		try {
			global.accountRestrictions = JSON.parse(global.accountRestrictions);
		} catch (error) {
			console.warn('Failed to parse accountRestrictions:', error);
			global.accountRestrictions = null;
		}
	}
};

export const determineAutoDiscoveryFips = (currentFips) => {
	if (
		currentFips === null &&
		global.allowAutoDiscovery === 'true' &&
		global.autoDiscoveryFips?.length > 0
	) {
		return global.autoDiscoveryFips;
	}
	return currentFips;
};

